// File: apps/web/src/main.tsx
// Version: 2026.8.3 | ForgeTrack entry point
// v2026.8.3: clientId/tenantId/audience now come from requiredEnv — no
//            hardcoded identity defaults, so a tenant move is config-only.
// v2026.8.2: Coerce empty-string env vars to undefined. GitHub Actions
//            injects '' for unset secrets, and `??` only falls back on
//            null/undefined — so clearing VITE_CIAM_AUTHORITY to move off
//            External ID would have yielded authority:'' instead of the
//            standard login.microsoftonline.com/{tenantId} fallback in
//            buildMsalConfig. apps/radar/src/main.tsx already does this.
// v2026.4.1: Original version.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ForgeTrackAuthProvider, type ForgeTrackAuthConfig } from '@forgetrack/auth';
import { requiredEnv } from '@forgetrack/api-client';
import { LocaleProvider } from '@forgetrack/i18n';
import { ErrorBoundary } from '@forgetrack/ui';
import { routeTree } from './routeTree.gen';

// TanStack Router instance
const router = createRouter({ routeTree });

// Type-safe router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// MSAL configuration
const authConfig: ForgeTrackAuthConfig = {
  clientId: requiredEnv('VITE_AZURE_CLIENT_ID', import.meta.env.VITE_AZURE_CLIENT_ID),
  tenantId: requiredEnv('VITE_AZURE_TENANT_ID', import.meta.env.VITE_AZURE_TENANT_ID),
  redirectUri: window.location.origin,
  scopes: [`${requiredEnv('VITE_API_AUDIENCE', import.meta.env.VITE_API_AUDIENCE)}/.default`],
  appType: 'web',
  authority: import.meta.env.VITE_CIAM_AUTHORITY || undefined,
  knownAuthorities: import.meta.env.VITE_CIAM_KNOWN_AUTHORITY
    ? [import.meta.env.VITE_CIAM_KNOWN_AUTHORITY]
    : [],
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <ForgeTrackAuthProvider config={authConfig}>
        <LocaleProvider>
          <RouterProvider router={router} />
        </LocaleProvider>
      </ForgeTrackAuthProvider>
    </ErrorBoundary>
  </StrictMode>,
);
